import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { kycService } from '../../services/api';
import toast from 'react-hot-toast';
import { background_onboarding, logo } from '../../constants/icons';

const PlayerMandateBiometricVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token: paramToken } = useParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const {
    playerId: statePlayerId,
    mobile: stateMobile,
    token: stateToken,
    verification: stateVerification,
    inviteParams,
    rawQuery,
    email,
    inviteSentBy,
    senderType,
    agencyId,
    type,
  } = location.state || {};

  const mandateToken = stateToken || paramToken || null;
  const playerId = statePlayerId;
  const [loading, setLoading] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);

  const basePath = useMemo(() => {
    if (mandateToken) {
      return `/player-register-mandate/${mandateToken}`;
    }
    return '/player-register-mandate';
  }, [mandateToken]);

  const navigationState = useMemo(
    () => ({
      token: mandateToken,
      playerId,
      mobile: stateMobile,
      verification: stateVerification,
      inviteParams,
      rawQuery,
      email,
      inviteSentBy,
      senderType,
      agencyId,
      type,
    }),
    [
      mandateToken,
      playerId,
      stateMobile,
      stateVerification,
      inviteParams,
      rawQuery,
      email,
      inviteSentBy,
      senderType,
      agencyId,
      type,
    ]
  );

  useEffect(() => {
    if (!playerId) {
      toast.error('Player ID not found. Please reopen your invite link.');
      navigate(basePath, { replace: true });
      return;
    }

    // Start camera - video element is always rendered so ref will be available
    console.log('Starting camera');
    startCamera();

    return () => {
      // Cleanup camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [playerId, basePath, navigate]);

  const startCamera = async () => {
    try {
      console.log('Getting user media');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
        audio: false,
      });

      console.log('Stream:', stream);

      // Video element is always rendered, so ref should be available
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraReady(true);
        console.log('Camera ready');
      } else {
        // Fallback: if ref is somehow not ready, wait a bit and retry
        streamRef.current = stream;
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            setCameraReady(true);
            console.log('Camera ready (delayed)');
          } else {
            stream.getTracks().forEach((track) => track.stop());
            setError('Video element not ready. Please refresh the page.');
            toast.error('Failed to initialize camera. Please refresh the page.');
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please allow camera permissions and try again.');
      toast.error('Camera access denied. Please enable camera permissions.');
    }
  };

  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error('Camera not ready');
      return;
    }

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);

      // Convert to base64
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageData);

      // Stop camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      setCameraReady(false);
      setIsCapturing(false);
    } catch (err) {
      console.error('Error capturing image:', err);
      toast.error('Failed to capture image. Please try again.');
      setIsCapturing(false);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setVerificationStatus(null);
    setError(null);
    startCamera();
  };

  const verifyIdentity = async () => {
    if (!capturedImage || !playerId) {
      toast.error('Please capture a selfie first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Extract base64 data (remove data:image/jpeg;base64, prefix)
      const base64Data = capturedImage.split(',')[1];

      const response = await kycService.verifyBiometric(playerId, {
        selfie: base64Data,
      });

      if (response.success && response.verified) {
        setVerificationStatus('approved');
        toast.success('Identity verified successfully!');
        
        // Navigate to action page after a short delay
        setTimeout(() => {
          navigate(`${basePath}/action`, {
            state: {
              ...navigationState,
              verification: {
                ...stateVerification,
                id_verified: true,
              },
            },
          });
        }, 2000);
      } else {
        setVerificationStatus('failed');
        const errorMsg = response.error || 'Verification failed. Please ensure your face matches your profile photo and try again.';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setVerificationStatus('failed');
      const errorMsg = err?.response?.data?.message || 'Verification failed. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`${basePath}/verify`, {
      state: navigationState,
    });
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center p-4"
      style={{
        backgroundImage: `url(${background_onboarding})`,
      }}
    >
      <div className="bg-white p-6 max-w-[544px] w-full flex flex-col rounded-xl gap-6">
        <img src={logo} alt="Transfer Pitch" className="h-10 mb-2 mx-auto" />
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-[#1A212F]">
            Biometric Verification
          </h2>
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Please take a selfie to verify your identity. Make sure your face is clearly visible and well-lit.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {verificationStatus === 'approved' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-green-800 font-medium">
                Identity verified successfully! Redirecting...
              </p>
            </div>
          </div>
        )}

        {/* Camera Preview */}
        {!capturedImage && (
          <div className="relative w-full bg-gray-900 rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
            {/* Always render video element so ref is available */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />
            {/* Show loading overlay only when camera is not ready */}
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center text-white">
                  <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-2" />
                  <p>Starting camera...</p>
                </div>
              </div>
            )}
            {/* Face guide overlay - only show when camera is ready */}
            {cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="border-2 border-white rounded-full w-48 h-48 opacity-50"></div>
              </div>
            )}
          </div>
        )}

        {/* Captured Image Preview */}
        {capturedImage && (
          <div className="relative w-full bg-gray-900 rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
            <img
              src={capturedImage}
              alt="Captured selfie"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          {!capturedImage ? (
            <button
              onClick={captureSelfie}
              disabled={!cameraReady || isCapturing}
              className="w-full py-3 px-6 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {isCapturing ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  <span>Capturing...</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>Capture Selfie</span>
                </>
              )}
            </button>
          ) : (
            <>
              {verificationStatus !== 'approved' && (
                <>
                  <button
                    onClick={verifyIdentity}
                    disabled={loading}
                    className="w-full py-3 px-6 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>Verify Identity</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={retakePhoto}
                    disabled={loading}
                    className="w-full py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Retake Photo
                  </button>
                </>
              )}
            </>
          )}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Your selfie will be compared against your profile photo to verify your identity.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlayerMandateBiometricVerification;
